import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { fetchAiModels, type AiModelSummary } from "../api/client";
import type { SiteNavItem } from "../config/siteNav";

type Props = {
  item: SiteNavItem;
};

/** Close when pointer is farther than ~1/2" from the open menu cluster. */
const CLOSE_OUTSIDE_PX = 48;

type Rect = { left: number; top: number; right: number; bottom: number };

function unionRects(rects: DOMRect[]): Rect | null {
  if (!rects.length) return null;
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const r of rects) {
    left = Math.min(left, r.left);
    top = Math.min(top, r.top);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  return { left, top, right, bottom };
}

function pointInRect(x: number, y: number, r: Rect): boolean {
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

export function AiModelsNavDropdown({ item }: Props) {
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState<AiModelSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAiModels()
      .then(setModels)
      .catch(() => setModels([]))
      .finally(() => setLoading(false));
  }, []);

  const closeAll = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;

    const menuRects = (): DOMRect[] => {
      const rects: DOMRect[] = [];
      const trigger = rootRef.current?.querySelector(".nav-tab-link");
      if (trigger) rects.push(trigger.getBoundingClientRect());
      if (dropdownRef.current) rects.push(dropdownRef.current.getBoundingClientRect());
      return rects;
    };

    const onPointerMove = (e: PointerEvent) => {
      const union = unionRects(menuRects());
      if (!union) return;
      const closeZone = {
        left: union.left - CLOSE_OUTSIDE_PX,
        top: union.top - CLOSE_OUTSIDE_PX,
        right: union.right + CLOSE_OUTSIDE_PX,
        bottom: union.bottom + CLOSE_OUTSIDE_PX,
      };
      if (!pointInRect(e.clientX, e.clientY, closeZone)) {
        closeAll();
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (rootRef.current?.contains(target)) return;
      closeAll();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeAll();
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const runModel = (modelId: string) => {
    setBusyId(modelId);
    closeAll();
    navigate(`/ai-models/run/${encodeURIComponent(modelId)}`);
    setBusyId(null);
  };

  return (
    <div
      ref={rootRef}
      className="nav-tab nav-tab--ai-models"
      onMouseEnter={() => setOpen(true)}
      onFocus={() => setOpen(true)}
    >
      <NavLink
        to={item.href}
        className={({ isActive }) =>
          `nav-tab-link${isActive ? " nav-tab-link--active" : ""}`
        }
      >
        {item.label}
        <span className="nav-chevron" aria-hidden>
          ▾
        </span>
      </NavLink>
      <div
        ref={dropdownRef}
        className={`nav-dropdown nav-dropdown--wide${open ? " nav-dropdown--open" : ""}`}
        onMouseEnter={() => setOpen(true)}
      >
        <ul className="nav-dropdown-section">
          <li>
            <NavLink
              to="/ai-models/train"
              className="nav-dropdown-link nav-dropdown-link--train"
              onClick={closeAll}
            >
              Convert Your Data into AI Models
            </NavLink>
          </li>
        </ul>
        <p className="nav-dropdown-heading">Example Models</p>
        {loading && <p className="nav-dropdown-muted">Loading models…</p>}
        <ul>
          {models.map((model) => (
            <li key={model.id}>
              <button
                type="button"
                className="nav-dropdown-link nav-dropdown-button"
                disabled={busyId === model.id}
                onClick={() => runModel(model.id)}
                title={model.description}
              >
                {model.menu_label ?? model.name}
              </button>
            </li>
          ))}

          {!loading && !models.length && (
            <li className="nav-dropdown-muted">Models unavailable right now</li>
          )}
        </ul>
      </div>
    </div>
  );
}
